import { useEffect, useRef } from 'react';

export const DOMAIN_CACHE_BUMP_TYPES = [
  'cache_updated',
  'inventory_updated',
  'products_updated',
  'data_changed',
  'sales_updated',
  'purchases_updated',
  'customers_updated',
  'suppliers_updated',
  'orders_updated',
  'supplier_payment_recorded',
  'supplier_invoice_changed'
];

const DEFAULT_DEBOUNCE_MS = 120;

/**
 * Re-render bump when selected domain events fire.
 * Debounced so Jul-9 event fan-out does not re-render on every micro-event.
 *
 * @param {() => void} bump
 * @param {{ types?: string[], enabled?: boolean, useRaf?: boolean, debounceMs?: number }} [options]
 */
export function useDomainCacheBump(bump, options = {}) {
  const {
    types = DOMAIN_CACHE_BUMP_TYPES,
    enabled = true,
    useRaf = true,
    debounceMs = DEFAULT_DEBOUNCE_MS
  } = options;

  const bumpRef = useRef(bump);
  bumpRef.current = bump;
  const typesKey = types.join('|');

  useEffect(() => {
    if (!enabled) return undefined;

    let debounceTimer = null;
    let rafId = null;

    const runBump = () => {
      const fn = bumpRef.current;
      if (typeof fn !== 'function') return;
      if (useRaf) {
        if (rafId != null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = null;
          fn();
        });
      } else {
        fn();
      }
    };

    const scheduleBump = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        runBump();
      }, Math.max(0, debounceMs));
    };

    const onDomainEvent = (event) => {
      const type = event?.detail?.type;
      if (!type || !types.includes(type)) return;
      scheduleBump();
    };

    window.addEventListener('elkheir:domain-event', onDomainEvent);
    return () => {
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [enabled, typesKey, useRaf, debounceMs]);
}
